import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DASHBOARD_WIDGETS, type WidgetKey, type WidgetSize } from '@serviceportal/shared';
import {
  Activity,
  AlarmClock,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  FileClock,
  FileSignature,
  GripVertical,
  Hourglass,
  Inbox,
  LayoutGrid,
  MonitorX,
  PackageCheck,
  Plus,
  Receipt,
  RotateCcw,
  ShieldAlert,
  Siren,
  Ticket,
  Timer,
  UserCheck,
  UserPlus,
  UserRound,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { PriorityLabel } from '@/components/tickets';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageHeader } from '@/components/ui/form-field';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Select, Skeleton } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { auditActionLabel } from '@/lib/audit-labels';
import { useAuth } from '@/lib/auth';
import type { DashboardData } from '@/lib/types';
import { cn, formatDateTime, formatRelative } from '@/lib/utils';

type Tile = { key: WidgetKey; size: WidgetSize };

/** Symbol und Ziel je Kennzahl-Kachel. */
const STAT_META: Partial<Record<WidgetKey, { icon: LucideIcon; to?: string }>> = {
  'tickets.open': { icon: Ticket, to: '/tickets' },
  'tickets.mine': { icon: UserRound, to: '/tickets?assignee=me' },
  'tickets.unassigned': { icon: Inbox, to: '/tickets?assignee=unassigned' },
  'tickets.followUpsDue': { icon: AlarmClock, to: '/tickets?followUpDue=true' },
  'tickets.waiting': { icon: Hourglass, to: '/tickets' },
  'tickets.createdToday': { icon: Plus, to: '/tickets' },
  'tickets.slaBreached': { icon: Siren, to: '/tickets' },
  'repairs.inWork': { icon: Wrench, to: '/repairs' },
  'repairs.mine': { icon: UserRound, to: '/repairs?tab=mine' },
  'repairs.quotePending': { icon: FileClock, to: '/repairs?tab=open' },
  'repairs.waitingParts': { icon: Timer, to: '/repairs?tab=open' },
  'repairs.ready': { icon: PackageCheck, to: '/repairs?tab=ready' },
  'billing.ready': { icon: Receipt, to: '/billing' },
  'monitoring.problems': { icon: MonitorX, to: '/monitoring' },
  'contracts.expiring': { icon: FileSignature, to: '/contracts' },
  'users.active': { icon: UserCheck, to: '/admin/users' },
  'users.invited': { icon: UserPlus, to: '/admin/users' },
  'users.locked': { icon: ShieldAlert, to: '/admin/users' },
  'security.failedLogins': { icon: Activity, to: '/admin/audit' },
};

const SIZE_CLASS: Record<WidgetSize, string> = { sm: 'col-span-1', md: 'col-span-2', lg: 'col-span-2 xl:col-span-4 3xl:col-span-6 4xl:col-span-8' };
const SIZE_LABEL: Record<WidgetSize, string> = { sm: 'Klein', md: 'Mittel', lg: 'Breit' };
const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'short' });
const time = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });

function StatTile({ k, value, editing }: { k: WidgetKey; value: number | undefined; editing: boolean }) {
  const meta = STAT_META[k];
  const Icon = meta?.icon ?? LayoutGrid;
  const body = (
    <Card className={cn('h-full p-5 transition-colors', !editing && 'hover:border-primary/40')}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{DASHBOARD_WIDGETS[k].label}</p>
        <Icon className="size-4 shrink-0 text-muted-foreground" />
      </div>
      {value === undefined ? <Skeleton className="mt-3 h-8 w-16" /> : <p className={cn('mt-2 text-3xl font-semibold tabular-nums', k === 'tickets.slaBreached' && value > 0 && 'text-destructive')}>{value}</p>}
    </Card>
  );
  return meta?.to && !editing ? <Link to={meta.to}>{body}</Link> : body;
}

function ListCard({ title, description, children, footer }: { title: string; description: string; children: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {children}
        {footer}
      </CardContent>
    </Card>
  );
}

const Empty = ({ text }: { text: string }) => <p className="text-sm text-muted-foreground">{text}</p>;

function ListTile({ k, data }: { k: WidgetKey; data: DashboardData | undefined }) {
  const def = DASHBOARD_WIDGETS[k];
  const loading = <Skeleton className="h-32" />;
  switch (k) {
    case 'tickets.myList':
      return (
        <ListCard title={def.label} description={def.description} footer={<Link to="/tickets?assignee=me" className="mt-3 inline-block text-sm text-primary hover:underline">Alle meine Tickets</Link>}>
          {!data?.myTickets ? loading : data.myTickets.length === 0 ? <Empty text="Keine offenen Tickets – gut gemacht." /> : (
            <ul className="divide-y">
              {data.myTickets.map((t) => (
                <li key={t.id} className="py-2 first:pt-0">
                  <Link to={`/tickets/${t.id}`} className="flex items-start justify-between gap-3 hover:underline">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{t.subject}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {t.number} · {t.status}
                        {t.customer ? ` · ${t.customer}` : ''}
                      </span>
                    </span>
                    <PriorityLabel priority={t.priority} compact />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </ListCard>
      );
    case 'repairs.readyList':
      return (
        <ListCard title={def.label} description={def.description}>
          {!data?.readyRepairs ? loading : data.readyRepairs.length === 0 ? <Empty text="Keine Geräte abholbereit." /> : (
            <ul className="divide-y">
              {data.readyRepairs.map((r) => (
                <li key={r.id} className="py-2 first:pt-0">
                  <Link to={`/repairs/${r.id}`} className="flex justify-between gap-3 hover:underline">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{r.device}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {r.number} · {r.customer}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">{r.readyAt ? formatRelative(r.readyAt) : ''}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </ListCard>
      );
    case 'monitoring.list':
      return (
        <ListCard title={def.label} description={def.description}>
          {!data?.problems ? loading : data.problems.length === 0 ? <Empty text="Keine aktiven Probleme." /> : (
            <ul className="divide-y">
              {data.problems.map((p) => (
                <li key={p.id} className="flex justify-between gap-3 py-2 first:pt-0">
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{p.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{[p.host, p.customer].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">{formatRelative(p.startedAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </ListCard>
      );
    case 'calendar.upcoming':
      return (
        <ListCard title={def.label} description={def.description} footer={<Link to="/calendar" className="mt-3 inline-block text-sm text-primary hover:underline">Zum Kalender</Link>}>
          {!data?.appointments ? loading : data.appointments.length === 0 ? <Empty text="Keine Termine in den nächsten 7 Tagen." /> : (
            <ul className="divide-y">
              {data.appointments.map((a) => (
                <li key={a.id} className="flex items-start gap-3 py-2 first:pt-0">
                  <CalendarDays className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{a.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {time.format(new Date(a.startAt))}
                      {a.location ? ` · ${a.location}` : ''}
                      {a.ticket ? ` · ${a.ticket}` : a.repair ? ` · ${a.repair}` : ''}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </ListCard>
      );
    case 'security.logins': {
      const max = Math.max(1, ...(data?.logins7d?.map((d) => d.count) ?? [1]));
      return (
        <ListCard title={def.label} description={def.description}>
          {!data?.logins7d ? loading : (
            <div className="flex h-44 items-end gap-2 sm:gap-4" role="img" aria-label="Anmeldungen pro Tag">
              {data.logins7d.map((d) => (
                <div key={d.day} className="flex flex-1 flex-col items-center gap-2">
                  <span className="text-xs tabular-nums text-muted-foreground">{d.count}</span>
                  <div className="w-full max-w-12 rounded-t-md bg-primary/80" style={{ height: `${Math.max(4, (d.count / max) * 120)}px` }} title={`${d.day}: ${d.count}`} />
                  <span className="text-xs text-muted-foreground">{weekday.format(new Date(`${d.day}T12:00:00`))}</span>
                </div>
              ))}
            </div>
          )}
        </ListCard>
      );
    }
    case 'audit.recent':
      return (
        <ListCard title={def.label} description={def.description} footer={<Link to="/admin/audit" className="mt-3 inline-block text-sm text-primary hover:underline">Gesamtes Audit-Log</Link>}>
          {!data?.recentActivity ? loading : (
            <ul className="flex flex-col divide-y">
              {data.recentActivity.map((a) => (
                <li key={a.id} className="flex items-start justify-between gap-3 py-2.5 first:pt-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{auditActionLabel(a.action)}</p>
                    <p className="truncate text-xs text-muted-foreground">{a.actorLabel ?? 'System'}</p>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground" title={formatDateTime(a.createdAt)}>
                    {formatRelative(a.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </ListCard>
      );
    default:
      return null;
  }
}

export function DashboardPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['dashboard'], queryFn: () => api<DashboardData>('/dashboard') });
  const [editing, setEditing] = useState(false);
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [adding, setAdding] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  useEffect(() => {
    if (data && !editing) setTiles(data.layout.filter((w): w is Tile => w.key in DASHBOARD_WIDGETS));
  }, [data, editing]);

  const save = useMutation({
    mutationFn: () => api('/dashboard/layout', { method: 'PUT', body: { layout: tiles } }),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Dashboard gespeichert');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const reset = useMutation({
    mutationFn: () => api('/dashboard/layout', { method: 'DELETE' }),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Standardanordnung wiederhergestellt');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const move = (from: number, to: number) =>
    setTiles((t) => {
      if (to < 0 || to >= t.length || from === to) return t;
      const next = [...t];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item!);
      return next;
    });
  const available = (data?.available ?? []).filter((k): k is WidgetKey => k in DASHBOARD_WIDGETS && !tiles.some((t) => t.key === k));
  const groups = [...new Set(available.map((k) => DASHBOARD_WIDGETS[k].group))];
  // Nach dem Hinzufügen fehlen die Daten der neuen Kachel bis zum Speichern; Kennzahlen zeigen dann einen Platzhalter.

  return (
    <>
      <PageHeader
        title={`Guten Tag, ${me?.user.firstName}`}
        description={`Überblick für ${me?.tenant.name}`}
        actions={
          editing ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" onClick={() => reset.mutate()} loading={reset.isPending}>
                <RotateCcw /> Standard
              </Button>
              <Button variant="outline" onClick={() => setAdding(true)} disabled={available.length === 0}>
                <Plus /> Kachel hinzufügen
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)}>
                Abbrechen
              </Button>
              <Button onClick={() => save.mutate()} loading={save.isPending}>
                Fertig
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setEditing(true)} disabled={!data}>
              <LayoutGrid /> Anpassen
            </Button>
          )
        }
      />
      {editing && <p className="mb-3 text-sm text-muted-foreground">Kacheln per Ziehen oder mit den Pfeilen verschieben, Größe wählen oder entfernen. Die Anordnung gilt nur für Sie.</p>}
      {!data ? (
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : tiles.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Keine Kacheln ausgewählt.{' '}
          <button type="button" className="text-primary hover:underline" onClick={() => (setEditing(true), setAdding(true))}>
            Kachel hinzufügen
          </button>
        </Card>
      ) : (
        <div className="grid grid-flow-row-dense grid-cols-2 gap-4 xl:grid-cols-4 3xl:grid-cols-6 4xl:grid-cols-8">
          {tiles.map((t, i) => {
            const def = DASHBOARD_WIDGETS[t.key];
            return (
              <div
                key={t.key}
                className={cn(SIZE_CLASS[t.size], editing && 'relative rounded-xl outline-2 outline-offset-4 outline-dashed outline-primary/40', dragIndex === i && 'opacity-50')}
                draggable={editing}
                onDragStart={() => setDragIndex(i)}
                onDragEnd={() => setDragIndex(null)}
                onDragOver={(e) => editing && e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex !== null) move(dragIndex, i);
                  setDragIndex(null);
                }}
              >
                {editing && (
                  <div className="absolute inset-x-2 -top-3 z-10 flex items-center justify-between gap-1 rounded-md border bg-card px-1 py-0.5 shadow-sm">
                    <span className="inline-flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
                      <GripVertical className="size-3.5 shrink-0 cursor-grab" aria-hidden />
                      <span className="truncate">{def.label}</span>
                    </span>
                    <span className="flex shrink-0 items-center">
                      <Button variant="ghost" size="icon" className="size-6" onClick={() => move(i, i - 1)} disabled={i === 0} aria-label={`${def.label} nach vorne`}>
                        <ArrowLeft className="size-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="size-6" onClick={() => move(i, i + 1)} disabled={i === tiles.length - 1} aria-label={`${def.label} nach hinten`}>
                        <ArrowRight className="size-3.5" />
                      </Button>
                      {def.sizes.length > 1 && (
                        <Select
                          className="h-6 w-20 px-1 text-xs"
                          value={t.size}
                          aria-label={`Größe von ${def.label}`}
                          onChange={(e) => setTiles((all) => all.map((x, j) => (j === i ? { ...x, size: e.target.value as WidgetSize } : x)))}
                        >
                          {def.sizes.map((s) => (
                            <option key={s} value={s}>
                              {SIZE_LABEL[s]}
                            </option>
                          ))}
                        </Select>
                      )}
                      <Button variant="ghost" size="icon" className="size-6 text-destructive hover:text-destructive" onClick={() => setTiles((all) => all.filter((_, j) => j !== i))} aria-label={`${def.label} entfernen`}>
                        <X className="size-3.5" />
                      </Button>
                    </span>
                  </div>
                )}
                <div className={cn('h-full', editing && 'pointer-events-none select-none')}>
                  {def.kind === 'stat' ? <StatTile k={t.key} value={data.stats[t.key]} editing={editing} /> : <ListTile k={t.key} data={data} />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent wide>
          <DialogHeader>
            <DialogTitle>Kachel hinzufügen</DialogTitle>
            <DialogDescription>Angeboten werden nur Kacheln, für die Sie die Berechtigung haben.</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            {available.length === 0 && <p className="text-sm text-muted-foreground">Alle verfügbaren Kacheln sind bereits auf Ihrem Dashboard.</p>}
            {groups.map((g) => (
              <section key={g}>
                <h3 className="mb-2 text-sm font-medium">{g}</h3>
                <div className="grid gap-2 sm:grid-cols-2">
                  {available
                    .filter((k) => DASHBOARD_WIDGETS[k].group === g)
                    .map((k) => (
                      <button
                        key={k}
                        type="button"
                        className="rounded-lg border p-3 text-left text-sm transition-colors hover:border-primary hover:bg-primary/5"
                        onClick={() => {
                          setTiles((t) => [...t, { key: k, size: DASHBOARD_WIDGETS[k].defaultSize }]);
                          toast.success(`„${DASHBOARD_WIDGETS[k].label}“ hinzugefügt – Daten erscheinen nach dem Speichern`);
                        }}
                      >
                        <span className="block font-medium">{DASHBOARD_WIDGETS[k].label}</span>
                        <span className="text-muted-foreground">{DASHBOARD_WIDGETS[k].description}</span>
                      </button>
                    ))}
                </div>
              </section>
            ))}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}
